import {
  ProvisionOrgWorkflowRequest,
  ProvisionWorkflowRequest,
  Workflow,
  WorkflowCondition,
  WorkflowsService,
} from '@/api';
import { action, atom } from 'nanostores';

/**
 * Loader state while loading projects
 */
export const $loadingWorkflows = atom<boolean>(false);

/**
 * The currently selected workflow.
 */
export const $workflow = atom<Workflow | undefined>();

/**
 * The list of workflows the user has access.
 */
export const $workflows = atom<Workflow[]>([]);

/**
 * Workflow builder states
 */
export const $addingNode = atom<boolean>(false);
export const $updatingNode = atom<boolean>(false);
export const $deletingNode = atom<boolean>(false);
export const $deletingEdge = atom<boolean>(false);

export const $selectedNode = atom<any>(null);

export const $savingNodeConfiguration = atom<boolean>(false);

// Method to open the drawer with the selected node
export const openNodeConfigurationDrawer = (node: any) => {
  $selectedNode.set(null);

  $selectedNode.set(node);
};

// Method to close the drawer
export const closeNodeConfigurationDrawer = () => {
  $selectedNode.set(null);
};

// Check if the drawer is open
export const $isDrawerOpen = atom<boolean>(false);

export const setDrawerOpen = (isOpen: boolean) => {
  $isDrawerOpen.set(isOpen);
};
/**
 * Main function to load workflows.
 */
const loadWorkflows = async (organizationId: string, workspaceId: string) => {
  console.log('Retrieving projects...');
  $loadingWorkflows.set(true); // Set loading state to true

  await ensureOrganization(organizationId);
  await ensureWorkspace(workspaceId);

  var workflows = await WorkflowsService.workflows(workspaceId);

  $workflows.set(workflows);

  $loadingWorkflows.set(false); // Set loading state back to false
};

/**
 * Watch for route changes; if we haven't loaded the projects, we try to load them.
 */
route.subscribe(async name => {
  if (
    (name !== 'workflows' && name !== 'workflow') ||
    ($workflows.value?.length ?? 0) > 0
  ) {
    return;
  }

  const params = $router.get()?.params as Record<'organizationId', string> &
    Record<'workspaceId', string>;

  await ensureWorkspace(params.workspaceId);
  await ensureOrganization(params.organizationId);
});

/**
 * Create a new workflow
 * TODO: Include profile selection/invites.
 */
export const createWorkflow = action(
  $workflow,
  'createWorkflow',
  async (
    store,
    name: string,
    orgId: string,
    workspaceId: string,
    isBulk: boolean,
    portfolioIds: string[],
  ) => {
    const request: ProvisionWorkflowRequest = {
      name: name,
      orgId: orgId,
      workspaceId: workspaceId,
      isBulk: isBulk,
      portfolioIds: portfolioIds,
    };

    const response = await WorkflowsService.provisionWorkflow(request);

    if (!response.workflow) {
      // error is displayed by caller
      return response;
    }

    $workflow.set(response.workflow);

    $workflows.set([response.workflow, ...$workflows.get()]);
    return response;
  },
);

/**
 * Create a new org level workflow
 * TODO: Include profile selection/invites.
 */
export const createOrgWorkflow = action(
  $workflow,
  'createOrgWorkflow',
  async (
    store,
    name: string,
    orgId: string,
    isBulk: boolean,
    portfolioIds: string[],
    condition: WorkflowCondition,
    timeZone: string,
  ) => {
    const request: ProvisionOrgWorkflowRequest = {
      name: name,
      orgId: orgId,
      isBulk: isBulk,
      portfolioIds: portfolioIds,
      condition: condition,
      timeZone: timeZone,
    };

    const response = await WorkflowsService.provisionOrgWorkflow(request);

    if (!response.workflow) {
      // error is displayed by caller
      return response;
    }

    $workflow.set(response.workflow);

    $workflows.set([response.workflow, ...$workflows.get()]);
    return response;
  },
);

/**
 * Delete's the selected workflow.
 */
export const deleteWorkflow = action(
  $project,
  'deletePWorkflow',
  async (store, workflowId: string) => {
    await WorkflowsService.deleteWorkflow(workflowId);

    $workflow.set(undefined);

    // Splice out the project
    const workflows = $workflows.get();

    const index = workflows.findIndex(w => w.id === workflowId);

    if (index > -1) {
      workflows.splice(index, 1);

      $workflows.set(workflows);
    }
  },
);
